import PermissionButton from '../../permissions/PermissionButton';
import React, { useEffect } from "react";
import {
  Form,
  Input,
  Checkbox,
  Button,
  Card,
  Row,
  Col,
  Typography,
  message,
  Select,
  Slider,
  Tabs,
} from "antd";
import { useUpdateClient, useFetchClientById } from "../../hooks/clientHooks";
import {
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  ScheduleOutlined,
  BankOutlined,
  PercentageOutlined,
  MoneyCollectOutlined,
  IdcardOutlined,
  MailOutlined,
  GlobalOutlined,
  FileTextOutlined,
  TeamOutlined,
  ShopOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import CommercialSelect from "../com/CommercialSelect";
import { useCities, usePaymentModes } from '../../hooks/listsHooks';

const { Option } = Select;
const { Title } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const ClientUpdateForm = ({ clientId, onFinishedUpdate }) => {
  
  const { client, loading: fetching } = useFetchClientById(clientId);
  const { handleUpdate, isUpdated } = useUpdateClient();
  const [form] = Form.useForm();
  const { cities } = useCities();
  const { modes: paymentModes } = usePaymentModes();

  useEffect(() => {
    if (client) {
      // Ensure boolean fields are correctly set
      const formValues = {
        ...client,
        BLOQUER: Boolean(client.BLOQUER),
        groupe: Boolean(client.groupe)
      };
      form.setFieldsValue(formValues);
    }
  }, [client, form]);

  const onFinish = async (values) => {
    try {
      await handleUpdate(clientId, {
        ...values,
        ECHEANCE: parseInt(values.ECHEANCE, 10) || 0,
        REMISE_G: parseFloat(values.REMISE_G) || 0.0,
        PLAFOND_SLD: parseFloat(values.PLAFOND_SLD) || 0.0,
        CODE_COM: parseInt(values.CODE_COM, 10) || 0,
        SOLDE: parseFloat(values.SOLDE) || 0.0,
        CODE_CAT: values.CODE_CAT ? parseInt(values.CODE_CAT, 10) : null,
        plaf_ech: values.plaf_ech ? parseFloat(values.plaf_ech) : null,
        objectif: values.objectif ? parseFloat(values.objectif) : null,
        remise_sp: values.remise_sp ? parseFloat(values.remise_sp) : null,
        BLOQUER: values.BLOQUER === true,
        groupe: values.groupe === true,
      });

      onFinishedUpdate();
    } catch (error) {
      message.error("Échec de la mise à jour: " + error.message);
    }
  };

  if (fetching) return <p>Chargement...</p>;

  return (
    <Card bordered={false} style={{ maxWidth: 1000, margin: "20px auto" }}>
      <Title level={4}>Mise à jour du Client</Title>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Tabs defaultActiveKey="1">
          <TabPane tab="Informations de Base" key="1">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="CLIENT"
                  label={<span><UserOutlined /> Nom du Client</span>}
                  rules={[{ required: true, message: 'Veuillez entrer le nom du client!' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="COMPTE"
                  label={<span><IdcardOutlined /> Numéro de Compte</span>}
                  rules={[{ required: true, message: 'Veuillez entrer le numéro de compte!' }]}
                >
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  name="CODE_CLT"
                  label={<span><IdcardOutlined /> Code Client</span>}
                >
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  name="TEL"
                  label={<span><PhoneOutlined /> Téléphone</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="MOBILE"
                  label={<span><PhoneOutlined /> Mobile</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="CONTACT"
                  label={<span><ContactsOutlined /> Personne à Contacter</span>}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label={<span><MailOutlined /> Email</span>}
                >
                  <Input type="email" />
                </Form.Item>
                <Form.Item
                  name="VILLE"
                  label={<span><EnvironmentOutlined /> Ville</span>}
                  rules={[{ required: true, message: 'Veuillez sélectionner une ville!' }]}
                >
                  <Select placeholder="Sélectionner une ville">
                    {cities.map((city) => (
                      <Option key={city} value={city}>
                        {city}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="PAYS"
                  label={<span><GlobalOutlined /> Pays</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="groupe"
                  label={<span><TeamOutlined /> Client Groupe</span>}
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
                <Form.Item
                  name="BLOQUER"
                  label={<span><MoneyCollectOutlined /> Bloqué</span>}
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
                <Form.Item
                  name="NOTES"
                  label={<span><FileTextOutlined /> Notes</span>}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
          
          <TabPane tab="Adresses" key="2">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="ADRESSE_SIEGE"
                  label={<span><HomeOutlined /> Adresse Siège</span>}
                >
                  <TextArea rows={3} />
                </Form.Item>
                <Form.Item
                  name="ADRESSE_LIV"
                  label={<span><HomeOutlined /> Adresse Livraison</span>}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CODE_PST"
                  label={<span><IdcardOutlined /> Code Postal</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="nat_clt"
                  label={<span><IdcardOutlined /> Nature Client</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="port_p"
                  label={<span><FileTextOutlined /> Port</span>}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
          
          <TabPane tab="Informations Financières" key="3">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="ECHEANCE"
                  label={<span><ScheduleOutlined /> Jours d'Échéance</span>}
                >
                  <Input type="number" />
                </Form.Item>
                <Form.Item
                  name="MODE_PAIE"
                  label={<span><BankOutlined /> Mode de Paiement</span>}
                >
                  <Select placeholder="Sélectionner un mode de paiement">
                    {paymentModes.map((mode) => (
                      <Option key={mode} value={mode}>
                        {mode}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="mode_paie_final"
                  label={<span><BankOutlined /> Mode de Paiement Final</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="conditions"
                  label={<span><FileTextOutlined /> Conditions</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="PLAFOND_SLD"
                  label={<span><DollarOutlined /> Limite de Crédit</span>}
                >
                  <Input type="number" />
                </Form.Item>
                <Form.Item
                  name="plaf_ech"
                  label={<span><DollarOutlined /> Limite d'Échéance</span>}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="REMISE_G"
                  label={<span><PercentageOutlined /> Remise Générale (%)</span>}
                >
                  <Slider
                    min={0}
                    max={100}
                    marks={{ 0: "0%", 50: "50%", 100: "100%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="remise_sp"
                  label={<span><PercentageOutlined /> Remise Spéciale (%)</span>}
                >
                  <Input type="number" />
                </Form.Item>
                <Form.Item
                  name="SOLDE"
                  label={<span><DollarOutlined /> Solde</span>}
                >
                  <Input type="number" />
                </Form.Item>
                <Form.Item
                  name="objectif"
                  label={<span><DollarOutlined /> Objectif</span>}
                >
                  <Input type="number" />
                </Form.Item>
                <Form.Item
                  name="CODE_COM"
                  label={<span><UserOutlined /> Commercial</span>}
                >
                  <CommercialSelect />
                </Form.Item>
                <Form.Item
                  name="CODE_CAT"
                  label={<span><ShopOutlined /> Code Catégorie</span>}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
          
          <TabPane tab="Informations Supplémentaires" key="4">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="BANQUE"
                  label={<span><BankOutlined /> Banque</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="COMPTEBNQ"
                  label={<span><BankOutlined /> Compte Bancaire</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="ICE"
                  label={<span><IdcardOutlined /> ICE</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="PATENTE"
                  label={<span><FileTextOutlined /> Patente</span>}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="CAPSOC"
                  label={<span><DollarOutlined /> Capital Social</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="FORMJUR"
                  label={<span><FileTextOutlined /> Forme Juridique</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="MODELIV"
                  label={<span><FileTextOutlined /> Mode de Livraison</span>}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="reg_trans"
                  label={<span><FileTextOutlined /> Réglementation des Transactions</span>}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
        <Row justify="end" style={{ marginTop: 20 }}>
          <Col>
            <PermissionButton permissionModule="clients" permissionAction="update" type="primary" htmlType="submit">
              Mettre à Jour le Client
            </PermissionButton>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default ClientUpdateForm;
